import { memberPriceMapKey, type PriceMap } from "@/lib/pricing"

/**
 * Member ("at member pricing") figures come solely from Stripe, via the
 * `get-products` feed's `member_price`. `fetchPrices` piggybacks them into the
 * `prices` / `fullPrices` maps under a `member:`-prefixed key (see
 * `memberPriceMapKey`). These helpers read them back so callers don't touch the
 * prefix. CHF-only in practice: the feed is CHF, so EUR markets have no member
 * keys and both helpers return null there (callers already gate on currency).
 */

/** Raw member price in the map's currency (CHF), or null when absent. */
export function memberPriceChf(
  prices: PriceMap,
  key: keyof PriceMap
): number | null {
  const v = prices[memberPriceMapKey(key)]
  return v ? Number(v) : null
}

/** Formatted member price label (e.g. "CHF 45"), or null when absent. Reads the
 *  `fullPrices` map, so it carries the locale-formatted currency. */
export function memberPriceLabelChf(
  fullPrices: PriceMap,
  key: keyof PriceMap
): string | null {
  return fullPrices[memberPriceMapKey(key)] ?? null
}

/** Price-map keys of the three MRI packages (Core, Advanced, Pro). */
export const MRI_PACKAGE_PRICE_KEYS = ["core", "advanced", "pro"] as const

/** Price-map keys of every add-on the member price applies to: the imaging
 *  add-ons and the blood add-ons (see PRODUCT_PRICE_KEYS in lib/pricing.ts). */
export const ADDON_PRICE_KEYS = [
  "addonBodyComposition",
  "addonGenderHealth",
  "addonHeartCT",
  "addonMammaMRI",
  "addonDEXA",
  "addonBVitamins",
  "addonOmega",
  "addonHormoneWomen",
  "addonHormoneMen",
  "addonCortisol",
  "addonLpa",
  "addonPsa",
  "addonTraceElements",
  "addonHeavyMetals",
  "addonCoeliac",
] as const

/**
 * Member saving (regular − member) for one product, from the live feed only.
 * Null when either figure is missing or the member price is not lower, so a
 * caller can never print a saving that Stripe does not back.
 */
export function memberSaving(
  prices: PriceMap,
  key: keyof PriceMap
): number | null {
  const regular = Number(prices[key])
  const member = memberPriceChf(prices, key)
  if (!prices[key] || !Number.isFinite(regular) || member == null) return null
  // Cents-safe: Stripe amounts arrive as unit_amount / 100, so subtracting
  // two of them can leave float residue (129.9 − 119.9 = 10.000000000000014).
  const saving = Math.round((regular - member) * 100) / 100
  return saving > 0 ? saving : null
}

/**
 * The largest member saving across `keys` — the honest figure behind an
 * "up to CHF X off" claim. Null when no key has a live regular + member pair.
 */
export function maxMemberSaving(
  prices: PriceMap | null | undefined,
  keys: readonly string[]
): number | null {
  if (!prices) return null
  let max: number | null = null
  for (const key of keys) {
    const s = memberSaving(prices, key)
    if (s != null && (max == null || s > max)) max = s
  }
  return max
}

/** Live regular + member pair for one product, or null when the feed lacks
 *  either figure. For surfaces that show "regular → member" and must hide the
 *  pair rather than fall back to a literal. */
export function liveRegularAndMember(
  prices: PriceMap | null | undefined,
  key: keyof PriceMap
): { regular: number; member: number } | null {
  if (!prices || !prices[key]) return null
  const regular = Number(prices[key])
  const member = memberPriceChf(prices, key)
  if (!Number.isFinite(regular) || member == null) return null
  return { regular, member }
}

/**
 * The "you save" figure for a member-pricing card. A card for one product
 * shows that product's saving; a card with `upToKeys` (full-body MRI, which is
 * sold as the Core/Advanced/Pro packages) shows the largest saving across them
 * and must be labelled "up to". Null when the feed backs neither.
 */
export function cardMemberSaving(
  prices: PriceMap | null | undefined,
  card: { priceKey?: string; upToKeys?: readonly string[] }
): { amount: number; upTo: boolean } | null {
  if (card.upToKeys) {
    const amount = maxMemberSaving(prices, card.upToKeys)
    return amount == null ? null : { amount, upTo: true }
  }
  if (!prices || !card.priceKey) return null
  const amount = memberSaving(prices, card.priceKey)
  return amount == null ? null : { amount, upTo: false }
}

/** MRI packages that carry their own advanced blood test. Core is the scan
 *  alone. */
const MRI_KEYS_WITH_BLOOD: ReadonlySet<string> = new Set(["advanced", "pro"])

/**
 * What an MRI package costs on top of the membership: its member price less
 * the membership's own yearly price, because both carry their own advanced
 * blood test. The booking app nets the scan the same way
 * (`bundleScanContributionCents`), so the site's figure and the basket's
 * agree. Core holds no blood test, so its member price is all it adds. Null
 * when the feed has no member price for the package.
 */
export function scanOnTopOfPlan(
  prices: PriceMap,
  key: keyof PriceMap,
  plan: number
): number | null {
  const member = memberPriceChf(prices, key)
  if (member == null) return null
  return MRI_KEYS_WITH_BLOOD.has(String(key)) ? member - plan : member
}
